// Import base form components that will be mapped to different field types
import BaseInput from "@/components/Base/BaseInput.vue";
import BaseSelect from "@/components/Base/BaseSelect.vue";
import BaseRadio from "@/components/Base/BaseRadio.vue";
import BaseMultiSelect from "@/components/Base/BaseMultiSelect.vue";
import BaseDateTimePicker from "@/components/Base/BaseDateTimePicker.vue";
import BaseFileUpload from "@/components/Base/BaseFileUpload.vue";
import BaseSwitch from "@/components/Base/BaseSwitch.vue";
import { phoneRegex, zipRegex, emailRegex } from "./validation.js";
import i18n from "@/i18n/index";

/**
 * Maps field types to their corresponding Vue components.
 * This centralizes the relationship between data types and UI components,
 * making it easy to maintain and extend the form rendering system.
 */
export const FIELD_COMPONENT_MAP = {
  text: BaseInput,
  password: BaseInput,
  document: BaseFileUpload,
  currency: BaseInput,
  contact: BaseInput,
  address: BaseInput,
  number: BaseInput,
  number_no_format: BaseInput,
  image: BaseFileUpload,
  percent: BaseInput,
  attachment: BaseFileUpload,
  boolean: BaseSwitch,
  dropdown: BaseSelect,
  us_state: BaseSelect,
  object: BaseSelect,
  object_array: BaseMultiSelect,
  time: BaseDateTimePicker,
  date: BaseDateTimePicker,
  date_time: BaseDateTimePicker,
  thumbnail: BaseFileUpload,
  video: BaseFileUpload,
  city: BaseInput,
  street_1: BaseInput,
  street_2: BaseInput,
  zip: BaseInput,
  phone: BaseInput,
};

/**
 * Maps component names to their actual implementations.
 * Provides a reverse lookup for components when needed by name rather than field type.
 */
export const COMPONENT_NAME_MAP = {
  BaseInput,
  BaseSelect,
  BaseRadio,
  BaseMultiSelect,
  BaseDateTimePicker,
  BaseFileUpload,
  BaseSwitch,
};

/**
 * Constants for all supported field types in the application.
 * Using constants prevents typos and makes code more maintainable.
 */
// NOSONAR
export const FIELD_TYPES = {
  TEXT: "text",
  PASSWORD: "password", // NOSONAR - input type, not a hardcoded credential
  DOCUMENT: "document",
  CURRENCY: "currency",
  CONTACT: "contact",
  ADDRESS: "address",
  NUMBER: "number",
  NUMBER_NO_FORMAT: "number_no_format",
  IMAGE: "image",
  PERCENT: "percent",
  ATTACHMENT: "attachment",
  TIME: "time",
  THUMBNAIL: "thumbnail",
  BOOLEAN: "boolean",
  DROPDOWN: "dropdown",
  US_STATE: "us_state",
  OBJECT: "object",
  VIDEO: "video",
  OBJECT_ARRAY: "object_array",
  DATE: "date",
  DATE_TIME: "date_time",
  CITY: "city",
  STREET_1: "street_1",
  STREET_2: "street_2",
  ZIP: "zip",
  PHONE: "phone",
  EMAIL: "email",
};

/**
 * Maps field types to appropriate HTML input types.
 * Ensures proper browser handling and validation for different data types.
 */
// NOSONAR
export const INPUT_TYPE_MAPPING = {
  [FIELD_TYPES.TEXT]: "text",
  [FIELD_TYPES.PASSWORD]: "password", // NOSONAR - input type, not a hardcoded credential
  [FIELD_TYPES.CURRENCY]: "number",
  [FIELD_TYPES.CONTACT]: "text",
  [FIELD_TYPES.ADDRESS]: "text",
  [FIELD_TYPES.NUMBER]: "number",
  [FIELD_TYPES.NUMBER_NO_FORMAT]: "number",
  [FIELD_TYPES.PERCENT]: "number",
  [FIELD_TYPES.TIME]: "time",
  [FIELD_TYPES.DATE]: "date",
  [FIELD_TYPES.DATE_TIME]: "datetime-local",
  [FIELD_TYPES.CITY]: "text",
  [FIELD_TYPES.STREET_1]: "text",
  [FIELD_TYPES.STREET_2]: "text",
  [FIELD_TYPES.ZIP]: "text",
  [FIELD_TYPES.PHONE]: "tel",
  [FIELD_TYPES.EMAIL]: "email",
};

/**
 * Defines acceptable file types for different file field types.
 * Provides user guidance and prevents invalid file uploads.
 */
export const FILE_ACCEPT_MAPPING = {
  [FIELD_TYPES.DOCUMENT]: ".pdf,.doc,.docx,.txt,.xls,.xlsx",
  [FIELD_TYPES.IMAGE]: "image/*",
  [FIELD_TYPES.ATTACHMENT]: "*/*",
  [FIELD_TYPES.THUMBNAIL]: ".png,.jpg,.jpeg,.gif,.bmp,.webp,.svg",
  [FIELD_TYPES.VIDEO]: "video/*",
};

/**
 * Defines allowed file types for validation (more specific than accept attribute)
 */
export const ALLOWED_FILE_TYPES = {
  [FIELD_TYPES.THUMBNAIL]: [
    "image/png",
    "image/jpeg",
    "image/gif",
    "image/bmp",
    "image/webp",
    "image/svg+xml",
  ],
  [FIELD_TYPES.IMAGE]: [
    "image/png",
    "image/jpeg",
    "image/gif",
    "image/bmp",
    "image/webp",
    "image/svg+xml",
  ],
  [FIELD_TYPES.VIDEO]: [
    "video/mp4",
    "video/avi",
    "video/mov",
    "video/wmv",
    "video/flv",
    "video/webm",
    "video/mkv",
  ],
  [FIELD_TYPES.DOCUMENT]: [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/plain",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ],
};

/**
 * Defines step values for numeric input types.
 * Controls the increment/decrement behavior for number inputs.
 */
export const NUMBER_STEP_MAPPING = {
  [FIELD_TYPES.CURRENCY]: "0.01", // Allow decimal places for currency
  [FIELD_TYPES.PERCENT]: "0.01", // Allow decimal places for percentages
  [FIELD_TYPES.NUMBER]: "1", // Whole numbers only
  [FIELD_TYPES.NUMBER_NO_FORMAT]: "1", // Whole numbers without formatting
};

/**
 * Field types that should not have maxLength validation.
 * These types either have fixed formats or don't use text input.
 */
const NO_MAXLENGTH_TYPES = [
  "date",
  "date_time",
  "time",
  "boolean",
  "dropdown",
  "us_state",
  "object",
  "object_array",
];

/**
 * List of all field types that represent file uploads.
 * Used to distinguish between regular fields and file fields.
 */
export const FILE_FIELD_TYPES = [
  "document",
  "image",
  "attachment",
  "thumbnail",
  "video",
];

/**
 * Regular expression to identify file field types.
 * Provides an alternative way to check for file fields using pattern matching.
 */
export const FILE_FIELD_REGEX = /document|image|attachment|thumbnail|video/;

/**
 * Determines if a field type represents a file upload field.
 * Centralizes the logic for identifying file fields throughout the application.
 */
export const isFileField = (fieldType) => {
  return FILE_FIELD_TYPES.includes(fieldType);
};

/**
 * Common validation patterns for specific field types.
 * Provides reusable regex patterns for consistent validation.
 */
export const VALIDATION_PATTERNS = {
  email: emailRegex, // Basic email validation
  phone: phoneRegex, // US phone number format +1 XXX XXX XXXX
  zip: zipRegex, // US ZIP code validation
};

/**
 * Checks if a value is empty, considering various empty states.
 * Handles strings, arrays, null, and undefined consistently.
 */
export const isEmpty = (value) => {
  return (
    value === "" ||
    value === null ||
    value === undefined ||
    (Array.isArray(value) && value.length === 0)
  );
};

/**
 * Validates a field value based on field configuration.
 * Performs type-specific validation and returns detailed error information.
 */
const validateRequiredField = (field, value) => {
  if (field.isRequired === "yes" && isEmpty(value)) {
    return `${field.displayTitle} ${i18n.global.t("documentLabels.fieldRequired")}`;
  }
  return null;
};

const validateFieldLength = (field, value) => {
  // Early return if value is empty (no further validation needed)
  // if (isEmpty(value)) return { isValid: errors.length === 0, errors };

  if (isEmpty(value)) return null;
  // Max length validation for appropriate field types
  if (
    field.maxLength &&
    field.maxLength > 0 &&
    !NO_MAXLENGTH_TYPES.includes(field.fieldType) &&
    typeof value === "string" &&
    value.length > field.maxLength
  ) {
    return `${field.displayTitle} must be no more than ${field.maxLength} characters`;
  }
  return null;
};

const validateCurrencyField = (value) => {
  if (isNaN(Number(value)) || Number(value) < 0) {
    return "Please enter a valid positive currency amount";
  }
  return null;
};

const validatePercentField = (value) => {
  const percentValue = Number(value);
  if (isNaN(percentValue) || percentValue < 0 || percentValue > 100) {
    return "Percentage must be between 0 and 100";
  }
  return null;
};

const validateNumberField = (value) => {
  if (isNaN(Number(value))) {
    return "Please enter a valid number";
  }
  return null;
};

const validateContactField = (value) => {
  if (!VALIDATION_PATTERNS.email.test(value)) {
    return "Please enter a valid email";
  }
  return null;
};

const validatePhoneField = (value) => {
  if (!value) {
    return "Please enter valid phone number";
  }
  return null;
};

const validateZipField = (value) => {
  if (value && !VALIDATION_PATTERNS.zip.test(value)) {
    return "Please enter a valid zip code(5 digits only)";
  }
  return null;
};

const validateEmailField = (value) => {
  if (value && !VALIDATION_PATTERNS.email.test(value)) {
    return "Please enter a valid email address";
  }
  return null;
};

const validateFieldType = (field, value) => {
  // Type-specific validation logic
  switch (field.fieldType) {
    case FIELD_TYPES.CURRENCY:
      return validateCurrencyField(value);
    case FIELD_TYPES.PERCENT:
      return validatePercentField(value);
    case FIELD_TYPES.NUMBER:
    case FIELD_TYPES.NUMBER_NO_FORMAT:
      return validateNumberField(value);
    case FIELD_TYPES.CONTACT:
      return validateContactField(value);
    case FIELD_TYPES.PHONE:
      return validatePhoneField(value);
    case FIELD_TYPES.ZIP:
      return validateZipField(value);
    case FIELD_TYPES.EMAIL:
      return validateEmailField(value);
    default:
      return null;
  }
};

export const validateField = (field, value) => {
  const errors = [];

  const requiredError = validateRequiredField(field, value);
  if (requiredError) errors.push(requiredError);

  if (isEmpty(value)) return { isValid: errors.length === 0, errors };

  const lengthError = validateFieldLength(field, value);
  if (lengthError) errors.push(lengthError);

  const typeError = validateFieldType(field, value);
  if (typeError) errors.push(typeError);

  return { isValid: errors.length === 0, errors };
};

/**
 * Formats file sizes into human-readable strings.
 * Converts bytes to appropriate units (KB, MB, GB) for display.
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Validates file type against allowed types for the field
 */
const validateFileType = (file, field) => {
  // Check if file is a valid File object
  if (!(file instanceof File) && !(file instanceof Blob)) {
    console.error("Invalid file object received:", file);
    return "Invalid file object. Please try uploading the file again.";
  }

  const allowedTypes = ALLOWED_FILE_TYPES[field.fieldType];
  if (!allowedTypes) return null;

  // If file type is missing or empty, try to determine it from the file name
  let fileType = file.type;
  if (!fileType && file.name) {
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext === "jpg" || ext === "jpeg") fileType = "image/jpeg";
    else if (ext === "png") fileType = "image/png";
    else if (ext === "gif") fileType = "image/gif";
    else if (ext === "bmp") fileType = "image/bmp";
    else if (ext === "webp") fileType = "image/webp";
    else if (ext === "svg") fileType = "image/svg+xml";
  }

  // Actually validate the file type
  if (!allowedTypes.includes(fileType)) {
    return `Invalid file type. Allowed types: ${allowedTypes.join(", ")}`;
  }

  return null;
};

/**
 * Validates file uploads based on field configuration.
 * Checks file size limits, file type, and required field validation.
 */
export const validateFile = (file, field) => {
  const errors = [];

  // If file is just an ID (number) and not a File object, skip validation
  // as it's likely already been uploaded
  if (file && typeof file === "number") {
    return { isValid: true, errors: [] };
  }

  // Required file validation
  if (!file) {
    if (field.isRequired === "yes") {
      errors.push(
        `${field.displayTitle} ${i18n.global.t("documentLabels.fieldRequired")}`,
      );
    }
    return { isValid: errors.length === 0, errors };
  }

  // File type validation
  const fileTypeError = validateFileType(file, field);
  if (fileTypeError) {
    errors.push(fileTypeError);
  }

  // File size validation
  if (field.maxLength && file.size > field.maxLength) {
    errors.push(
      `File size must be less than ${formatFileSize(field.maxLength)}`,
    );
  }

  return { isValid: errors.length === 0, errors };
};

/**
 * Converts API field definitions to component props.
 * This bridges the gap between backend field configurations and frontend component requirements.
 */
export const mapApiFieldToComponentProps = (field, overrides = {}) => {
  const isYearField = field.fieldName?.toLowerCase() === "year";
  const shouldSetMaxLength =
    field.maxLength > 0 && !NO_MAXLENGTH_TYPES.includes(field.fieldType);
  return {
    label: overrides.label,
    id: field.fieldName,
    name: field.fieldName,
    required: field.isRequired === "yes",
    readonly: field.readOnly === "yes",
    disabled: field.readOnly === "yes" || overrides.disabled === true,
    maxLength: (() => {
      if (isYearField) return 4;
      return shouldSetMaxLength ? field.maxLength : undefined;
    })(),
    helperText: field.helperText,
    placeholder: field.placeholder,
    type: INPUT_TYPE_MAPPING[field.fieldType] || "text",
    accept: FILE_ACCEPT_MAPPING[field.fieldType],
    isMultiple: field.isMultiple ?? overrides?.isMultiple ?? false,
    uiType:
      field.uiType ??
      overrides.uiType ??
      (["image", "thumbnail"].includes(field.fieldType)
        ? "round"
        : "rectangle"),
    step: NUMBER_STEP_MAPPING[field.fieldType],
    options: (() => {
      if (Array.isArray(field.variableOptions)) {
        return mapFieldOptions(field.variableOptions, field.fieldName);
      }
      return field.variableOptions
        ? parseVariableOptions(field.variableOptions, field.fieldName)
        : [];
    })(),
    encrypted: field.storeEncrypted === "yes",
    digitOnly: isYearField || field.fieldType === FIELD_TYPES.NUMBER_NO_FORMAT,
    inputmode:
      isYearField ||
      field.fieldType === FIELD_TYPES.NUMBER_NO_FORMAT ||
      field.fieldType === FIELD_TYPES.PHONE
        ? "numeric"
        : undefined,
    pattern: field.pattern || undefined,
    icon: field.icon || undefined,
    iconPosition: field.iconPosition || "left",
    modelValue: field.fieldType === FIELD_TYPES.BOOLEAN ? false : undefined,
  };
};

/**
 * Maps field option values to their translated labels.
 * Handles the mapping from API values like "birth" to translated values like "Nascimento" or "Birth".
 * Supports Relationship, PetType, Color, Policy Type, Content, and Beneficiaries fields dynamically using the i18n translation system.
 */
export const mapFieldOptions = (options, fieldName) => {
  if (!options || !Array.isArray(options)) return [];

  const normalizedFieldName = fieldName?.toLowerCase();

  // Only apply special mapping for supported field types
  const supportedFields = [
    "relationship",
    "pettype",
    "color",
    "policy type",
    "Policy Type",
    "content",
    "beneficiaries",
    "Relationship",
    "Asset",
    "form",
    "Form",
  ];
  if (!supportedFields.includes(normalizedFieldName)) {
    return options;
  }

  return options.map((option) => {
    const value = typeof option === "string" ? option.trim() : option.value;
    const normalizedValue = value
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[()]/g, "");

    // Determine the translation path based on the field name
    let translationPath;
    if (normalizedFieldName === "relationship") {
      translationPath = `templateFields.relationship.options.${normalizedValue}`;
    } else if (normalizedFieldName === "pettype") {
      translationPath = `templateFields.PetType.options.${normalizedValue}`;
    } else if (normalizedFieldName === "color") {
      translationPath = `templateFields.Color.options.${normalizedValue}`;
    } else if (
      normalizedFieldName === "policy type" ||
      normalizedFieldName === "Policy Type"
    ) {
      translationPath = `templateFields.Policy Type.options.${normalizedValue}`;
    } else if (normalizedFieldName === "content") {
      translationPath = `templateFields.Content.options.${normalizedValue}`;
    } else if (normalizedFieldName === "beneficiaries") {
      translationPath = `templateFields.Beneficiaries.options.${normalizedValue}`;
    } else if (normalizedFieldName === "Relationship") {
      translationPath = `templateFields.Relationship.options.${normalizedValue}`;
    } else if (normalizedFieldName === "Asset") {
      translationPath = `templateFields.Asset.options.${normalizedValue}`;
    } else if (
      normalizedFieldName === "form" ||
      normalizedFieldName === "Form"
    ) {
      translationPath = `templateFields.Form.options.${normalizedValue}`;
    }

    // Try to get the translated label
    if (translationPath && i18n.global.te(translationPath)) {
      const translatedLabel = i18n.global.t(translationPath);
      return {
        label: translatedLabel,
        value: value,
      };
    }

    // Fallback for unmapped options - use the original value
    return {
      label: typeof option === "string" ? option : option.label,
      value: value,
    };
  });
};

/**
 * Parses variable options from string format to structured format.
 * Converts comma-separated strings into option objects for select components.
 * Applies translation mapping for supported fields (relationship, pettype).
 */
export const parseVariableOptions = (variableOptions, fieldName = null) => {
  if (!variableOptions) return [];

  const options = variableOptions.split(",").map((option) => ({
    label: option.trim(),
    value: option.trim(),
  }));

  // Apply field-specific translation mapping
  return mapFieldOptions(options, fieldName);
};
